import { format, formatDistanceToNow, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";

import { HEARING_TYPES_MAPPING } from "@/features/claims/components/court/hearing-helpers";

import type { ProcedureRow } from "../api/use-procedures";

// biome-ignore-start lint/style/useNamingConvention: enum-valued keys
export const PROCEDURE_TYPE_LABELS: Record<ProcedureRow["procedureType"], string> = {
  PROCEDURE_CLASSIQUE: "Classique",
  ESCP: "ESCP",
};
// biome-ignore-end lint/style/useNamingConvention: enum-valued keys

/**
 * The API declares every date as `Date`, but hapi-swagger drops the `null` half
 * of the union — and it also serialises them as ISO strings over the wire. Both
 * are handled here so callers never juggle a string that TypeScript calls a Date.
 */
export function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "12/03/2026", or « — » when the date is missing. */
export function formatDate(value: Date | string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, "dd/MM/yyyy", { locale: fr }) : "—";
}

/** "12 mars 2026" — used where the column is wide enough to breathe. */
export function formatLongDate(value: Date | string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, "d MMMM yyyy", { locale: fr }) : "—";
}

/** « il y a 3 jours », or « jamais » when the procedure was never synced. */
export function formatRelativeDate(value: Date | string | null | undefined): string {
  const date = toDate(value);
  return date ? `il y a ${formatDistanceToNow(date, { locale: fr })}` : "jamais";
}

/**
 * Compact age for dense cells: "10 h", "3 j", "2 mois". `formatDistanceToNow`
 * spells out "il y a environ 10 heures", which is three times the width for the
 * same information.
 */
export function formatCompactAge(value: Date | string | null | undefined): string {
  const date = toDate(value);
  if (!date) {
    return "jamais";
  }

  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60_000));
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours} h`;
  }
  const days = Math.round(hours / 24);
  if (days < 31) {
    return `${days} j`;
  }
  const months = Math.round(days / 30);
  return months < 12 ? `${months} mois` : `${Math.round(months / 12)} an${months >= 24 ? "s" : ""}`;
}

/**
 * A registry document in the shape FilePreviewDialog expects. The payload is
 * flat (hapi-swagger cannot express a nullable nested object), so the mapping
 * lives here rather than being repeated at each call site.
 */
export function toPreviewFile(document: {
  fileId: string;
  fileName: string | null;
  fileMimeType: string | null;
  fileSize: number | null;
  fileBoxFileId: string | null;
}) {
  return {
    id: document.fileId,
    name: document.fileName,
    mimeType: document.fileMimeType,
    size: document.fileSize,
    boxFileId: document.fileBoxFileId,
  };
}

/** Month a row is grouped under, e.g. "mai 2026". */
export function formatMonth(value: Date | string | null | undefined): string | null {
  const date = toDate(value);
  return date ? format(date, "MMMM yyyy", { locale: fr }) : null;
}

type HearingTypeName = keyof typeof HEARING_TYPES_MAPPING;

/** French label of a hearing type, falling back to the raw name if unmapped. */
export function formatHearingTypeLabel(typeName: string | null | undefined): string | null {
  if (!typeName) {
    return null;
  }
  // The API types this as a plain string — unknown names fall back to themselves.
  return HEARING_TYPES_MAPPING[typeName as HearingTypeName]?.label ?? typeName;
}

/**
 * Courts abbreviated the way the team says them out loud — « TC Bobigny »,
 * « TAE Paris ». Longest prefix first, so « Tribunal de Commerce » cannot be
 * swallowed by a shorter pattern.
 */
const COURT_ABBREVIATIONS: ReadonlyArray<[RegExp, string]> = [
  [/^tribunal des activités économiques\b/i, "TAE"],
  [/^tribunal de commerce\b/i, "TC"],
  [/^tribunal judiciaire\b/i, "TJ"],
  [/^tribunal de proximité\b/i, "TP"],
  [/^cour d'appel\b/i, "CA"],
];

/**
 * The city a court sits in — « Tribunal de Commerce de Bobigny » → « Bobigny »,
 * « Tribunal des activités économiques de Lyon » → « Lyon ».
 *
 * Splitting on the *last* « de »/« d' » is what makes both work: the first one
 * belongs to the court's type (« de Commerce »), only the last introduces the
 * place. A name with neither is left alone rather than mangled.
 */
export function shortCourtName(name: string): string {
  const match = name.match(/(?:\sde\s|\sd')(?!.*(?:\sde\s|\sd'))(.+)$/);
  return match ? match[1].trim() : name;
}

/**
 * « TC Bobigny », « TAE Paris » — the abbreviation keeps the kind of court,
 * which the bare city dropped: Paris has both a TAE and a TJ, and knowing which
 * one changes who you are dealing with.
 */
export function abbreviatedCourtName(name: string): string {
  const city = shortCourtName(name);
  const abbreviation = COURT_ABBREVIATIONS.find(([pattern]) => pattern.test(name))?.[1];

  return abbreviation ? `${abbreviation} ${city}` : city;
}

/**
 * Counting forms for the month separator, taken from the canonical labels in
 * HEARING_TYPES_MAPPING rather than invented: « mise en état » names one precise
 * step of the procedure, not every procedural hearing, and using it as a synonym
 * was simply wrong.
 *
 * The three PROCEDURAL_* kinds collapse into one entry — a band does not need to
 * split hearings by who asked for the postponement.
 */
// biome-ignore-start lint/style/useNamingConvention: enum-valued keys
const HEARING_TYPE_COUNTS: Record<string, { one: string; many: string }> = {
  FIRST_HEARING: { one: "première audience", many: "premières audiences" },
  PROCEDURAL_HEARING: { one: "audience de procédure", many: "audiences de procédure" },
  PROCEDURAL_HEARING_FOLLOWING_POSTPONEMENT_BY_US: {
    one: "audience de procédure",
    many: "audiences de procédure",
  },
  PROCEDURAL_HEARING_FOLLOWING_POSTPONEMENT_BY_OPPOSING_PARTY: {
    one: "audience de procédure",
    many: "audiences de procédure",
  },
  PLEA_HEARING: { one: "plaidoirie", many: "plaidoiries" },
  DELIBERATION_HEARING: { one: "délibéré", many: "délibérés" },
};
// biome-ignore-end lint/style/useNamingConvention: enum-valued keys

function countingForm(typeName: string, count: number): string {
  const forms = HEARING_TYPE_COUNTS[typeName];
  return forms ? (count > 1 ? forms.many : forms.one) : (formatHearingTypeLabel(typeName) ?? typeName);
}

/**
 * A month band's tally: how many hearings it holds, then the breakdown by kind,
 * biggest first — « 12 audiences · 7 mises en état · 5 plaidoiries ».
 *
 * A single kind drops the total: « 1 audience · 1 délibéré » says the same thing
 * twice, so the band just reads « 1 délibéré ».
 *
 * Counts what is on screen, not the whole result set: the band spans the rows of
 * the current page, and claiming otherwise would be a lie the user cannot check.
 */
export function summariseHearings(procedures: ProcedureRow[]): string | null {
  const hearings = procedures.filter((procedure) => procedure.nextHearing);
  if (hearings.length === 0) {
    return null;
  }

  // Keyed on the counting form, so the three procedural kinds land in one entry.
  const byForm = new Map<string, { typeName: string; count: number }>();
  for (const { nextHearing } of hearings) {
    if (nextHearing) {
      const key = countingForm(nextHearing.typeName, 2);
      const entry = byForm.get(key);
      byForm.set(key, { typeName: nextHearing.typeName, count: (entry?.count ?? 0) + 1 });
    }
  }

  const breakdown = [...byForm.values()]
    .sort((a, b) => b.count - a.count || a.typeName.localeCompare(b.typeName))
    .map(({ typeName, count }) => `${count} ${countingForm(typeName, count)}`);

  if (breakdown.length === 1) {
    return breakdown[0];
  }

  return [`${hearings.length} audience${hearings.length > 1 ? "s" : ""}`, ...breakdown].join(" · ");
}

/** Cases enrolled this long ago are stalling — the milestone turns red. */
const STALE_ENROLMENT_MONTHS = 15;

export function isEnrolmentStale(value: Date | string | null | undefined): boolean {
  const date = toDate(value);
  if (!date) {
    return false;
  }

  const months = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  return months > STALE_ENROLMENT_MONTHS;
}

/** The hearing date of a row, preferring the precise ISO timestamp when present. */
export function getHearingDate(procedure: ProcedureRow): Date | null {
  if (!procedure.nextHearing) {
    return null;
  }
  return toDate(procedure.nextHearing.dateIso) ?? toDate(procedure.nextHearing.date);
}

/**
 * « 2 octobre 2026 – 09:30 », falling back to the bare day when no hour is known.
 *
 * The hour arrives already resolved from the API — it reconciles the court's
 * timezone-naive `hearing_date_iso` with the registry's declared hour, so no
 * client has to know that reading `dateIso` as an instant shifts 09:30 to 11:30
 * in Paris.
 */
export function formatHearingDateTime(procedure: ProcedureRow): string {
  const { nextHearing } = procedure;
  if (!nextHearing) {
    return "—";
  }

  const day = formatLongDate(nextHearing.date);
  return nextHearing.hour ? `${day} – ${nextHearing.hour}` : day;
}

export function getHearingsOnDay(procedures: ProcedureRow[], day: Date): ProcedureRow[] {
  return procedures.filter((procedure) => {
    const date = getHearingDate(procedure);
    return date !== null && isSameDay(date, day);
  });
}
